import type { AgenticLogicRuleDto } from "@intelligems/ig-types/dtos/agentic-logic/rules";

import type { AgenticLogicSystemRulePhase } from "@src/api/agentic-logic/ensure-agentic-logic-system-rule";
import type { AgenticLogicBuiltInRuleOption } from "@src/features/sharedExperience/components/agenticLogic/AgenticLogicBindingFields";

export const SHOPIFY_RECOMMENDATIONS_RULE_LABEL = "Shopify recommendations";

export interface CheckoutUpsellBuiltInRule {
  /** The pinned menu entry, or null when this block offers no built-in rule. */
  option: AgenticLogicBuiltInRuleOption | null;
  /**
   * True while the block is on — or about to be defaulted onto — the built-in
   * rule and that rule is not live yet. A preview then shows loading rather
   * than an empty or "no rule" block.
   */
  isAwaitingBuiltInRule: boolean;
}

/**
 * Whether a Checkout > Upsell block takes the built-in rule as its default.
 * Only a NEW block does: one that has never been saved and has made no rule
 * choice (`agenticLogicRuleId` undefined). A saved block keeps whatever it
 * saved — including no rule at all. The checkout-blocks listener binds such a
 * block when it is created (see `setupCheckoutBlocksListener`).
 */
export const takesCheckoutUpsellDefaultRule = (block: {
  isSavedBlock: boolean;
  blockRuleId: string | null | undefined;
}): boolean => !block.isSavedBlock && block.blockRuleId === undefined;

/**
 * The Shopify recommendations default for a Checkout > Upsell block, as a pure
 * function of the block and the store's built-in rule.
 *
 * `offersBuiltIn` is the scope: an engine org's checkout upsell or
 * post-purchase upsell block. Onsite containers get no built-in entry and no
 * default.
 */
export const deriveCheckoutUpsellBuiltInRule = (input: {
  offersBuiltIn: boolean;
  isSavedBlock: boolean;
  blockRuleId: string | null | undefined;
  systemRule: AgenticLogicRuleDto | null;
  phase: AgenticLogicSystemRulePhase;
}): CheckoutUpsellBuiltInRule => {
  if (!input.offersBuiltIn) {
    return { option: null, isAwaitingBuiltInRule: false };
  }
  const builtInRuleId = input.systemRule?.id ?? null;
  const takesDefault = takesCheckoutUpsellDefaultRule(input);
  const isOnBuiltIn =
    takesDefault ||
    (builtInRuleId !== null && input.blockRuleId === builtInRuleId);
  return {
    option: {
      label: SHOPIFY_RECOMMENDATIONS_RULE_LABEL,
      ruleId: builtInRuleId,
      description: input.systemRule?.description ?? null,
      phase: input.phase,
      isPendingDefault: takesDefault,
    },
    isAwaitingBuiltInRule: isOnBuiltIn && input.phase === "provisioning",
  };
};
